import React from 'react';
import { Flex, Select } from '@decisiv/ui-components';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import TextArea  from '@decisiv/ui-components/lib/components/TextArea';
import Checkbox  from '@decisiv/ui-components/lib/components/Checkbox';
import AdminTypeahead from './AdminTypeahead';

const renderField = (field, formState, setFormState, displays, setDisplays, formProps) => {
  const { name, label, type = 'text', span, required, helpMessage, rows, options, searchEndpoint } = field;
  const spanStyle = span === 2 ? { gridColumn: 'span 2' } : {};

  switch (type) {
    case 'text':
      return (
        <div key={name} style={spanStyle}>
          <TextField
            label={label}
            value={formState[name]}
            onChange={(e) => {
              const value = e.target.value;
              setFormState((prev) => ({ ...prev, [name]: value }));
            }}
            helpMessage={helpMessage}
            required={required}
          />
        </div>
      );

    case 'password':
      return (
        <div key={name} style={spanStyle}>
          <TextField
            label={label}
            value={formState[name]}
            onChange={(e) => {
              const value = e.target.value;
              setFormState((prev) => ({ ...prev, [name]: value }));
            }}
            helpMessage={helpMessage}
            required={required}
            inputProps={{ type: 'password' }}
          />
        </div>
      );

    case 'textarea':
      return (
        <div key={name} style={spanStyle}>
          <TextArea
            label={label}
            value={formState[name]}
            onChange={(e) => {
              const value = e.target.value;
              setFormState((prev) => ({ ...prev, [name]: value }));
            }}
            rows={rows}
          />
        </div>
      );

    case 'select': {
      const resolvedOptions = typeof options === 'function' ? options(formProps) : options ?? [];
      const selectOptions   = [
        { id: '', label: `Select ${label.toLowerCase()}…`, value: '' },
        ...resolvedOptions,
      ];
      return (
        <div key={name} style={spanStyle}>
          <Select
            label={label}
            options={selectOptions}
            value={formState[name]}
            onChange={(value) => setFormState((prev) => ({ ...prev, [name]: value }))}
          />
        </div>
      );
    }

    case 'typeahead':
      return (
        <div key={name} style={spanStyle}>
          <AdminTypeahead
            label={label}
            searchEndpoint={searchEndpoint}
            value={formState[name]}
            displayValue={displays[name] ?? ''}
            onChange={(value, display) => {
              setFormState((prev) => ({ ...prev, [name]: value }));
              setDisplays((prev) => ({ ...prev, [name]: display }));
            }}
            helpMessage={helpMessage}
          />
        </div>
      );

    default:
      return null;
  }
};

/**
 * Renders all form fields from a config array, split into a 2-col grid for
 * non-checkbox fields and a horizontal row for checkboxes.
 *
 * @param {Array}    fields        Full field config array from createAdminResource form config.
 * @param {object}   formState     Current form values keyed by field name.
 * @param {function} setFormState  State setter for formState.
 * @param {object}   displays      Display strings for typeahead fields.
 * @param {function} setDisplays   State setter for displays.
 * @param {object}   formProps     Extra props forwarded from the page component (e.g. select options).
 */
const FormFields = ({ fields = [], formState, setFormState, displays, setDisplays, formProps = {} }) => {
  const inputFields    = fields.filter((f) => f.type !== 'checkbox');
  const checkboxFields = fields.filter((f) => f.type === 'checkbox');

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
        {inputFields.map((field) =>
          renderField(field, formState, setFormState, displays, setDisplays, formProps)
        )}
      </div>

      {checkboxFields.length > 0 && (
        <Flex gap={2} marginTop={3}>
          {checkboxFields.map((field) => (
            <Checkbox
              key={field.name}
              label={field.label}
              checked={formState[field.name]}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormState((prev) => ({ ...prev, [field.name]: checked }));
              }}
            />
          ))}
        </Flex>
      )}
    </>
  );
};

export default FormFields;
